import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  Chip,
  Divider,
  IconButton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Computer as ComputerIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { AuditTrail } from '../services/auditTrailService';
import { format } from 'date-fns';

interface AuditTrailDetailModalProps {
  open: boolean;
  onClose: () => void;
  auditTrail: AuditTrail;
}

const AuditTrailDetailModal: React.FC<AuditTrailDetailModalProps> = ({
  open,
  onClose,
  auditTrail,
}) => {
  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'PPpp');
  };

  const getActionColor = (action: string) => {
    switch (action.toLowerCase()) {
      case 'create':
        return 'success';
      case 'update':
        return 'warning';
      case 'delete':
        return 'error';
      default:
        return 'default';
    }
  };

  const renderJsonData = (data: Record<string, any> | null, title: string) => {
    if (!data) return null;

    return (
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Box
          component="pre"
          sx={{
            backgroundColor: 'grey.50',
            p: 2,
            borderRadius: 1,
            overflow: 'auto',
            maxHeight: 300,
            fontSize: '0.875rem',
            fontFamily: 'monospace',
          }}
        >
          {JSON.stringify(data, null, 2)}
        </Box>
      </Paper>
    );
  };

  const getFieldChanges = () => {
    const oldData = auditTrail.old_data || {};
    const newData = auditTrail.new_data || {};
    const changes: Array<{field: string, previous: any, new: any, type: 'changed' | 'added' | 'removed'}> = [];

    // Get all unique keys from both old and new data
    const allKeys = new Set([...Object.keys(oldData), ...Object.keys(newData)]);
    
    allKeys.forEach(key => {
      const oldValue = oldData[key];
      const newValue = newData[key];
      
      if (oldValue === undefined && newValue !== undefined) {
        // Field was added
        changes.push({
          field: key,
          previous: null,
          new: newValue,
          type: 'added'
        });
      } else if (oldValue !== undefined && newValue === undefined) {
        // Field was removed
        changes.push({
          field: key,
          previous: oldValue,
          new: null,
          type: 'removed'
        });
      } else if (oldValue !== newValue) {
        // Field was changed
        changes.push({
          field: key,
          previous: oldValue,
          new: newValue,
          type: 'changed'
        });
      }
    });

    return changes;
  };

  const fieldChanges = getFieldChanges();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">
              Audit Trail Details
            </Typography>
            <Chip
              label={auditTrail.action}
              color={getActionColor(auditTrail.action) as any}
              size="small"
            />
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Record ID
                  </Typography>
                  <Typography variant="body1">{auditTrail.id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Client
                  </Typography>
                  <Typography variant="body1">
                    {auditTrail.client_name || `Client ID: ${auditTrail.client_id}`}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Area
                  </Typography>
                  <Chip label={auditTrail.area} size="small" variant="outlined" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Entity Type
                  </Typography>
                  <Typography variant="body1">{auditTrail.entity_type}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Entity ID
                  </Typography>
                  <Typography variant="body1">{auditTrail.entity_id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Source System
                  </Typography>
                  <Typography variant="body1">
                    {auditTrail.source_system || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Timing Information */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Timing Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ScheduleIcon fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Change Date
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(auditTrail.change_date)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ScheduleIcon fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Record Created
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(auditTrail.created_at)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* User Information */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                User Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        User
                      </Typography>
                      <Typography variant="body1">
                        {auditTrail.user_name || 'System'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ComputerIcon fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Session ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {auditTrail.session_id || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                {auditTrail.ip_address && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      IP Address
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {auditTrail.ip_address}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>

          {/* Reason */}
          {auditTrail.reason && (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <InfoIcon fontSize="small" />
                  <Typography variant="h6">
                    Reason
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {auditTrail.reason}
                </Typography>
              </Paper>
            </Grid>
          )}

          {/* Field Changes Table */}
          {fieldChanges.length > 0 && (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Field Changes ({fieldChanges.length})
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Field</strong></TableCell>
                        <TableCell><strong>Previous Value</strong></TableCell>
                        <TableCell><strong>New Value</strong></TableCell>
                        <TableCell><strong>Type</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fieldChanges.map((change, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {change.field}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontFamily: 'monospace',
                                color: change.type === 'removed' ? 'error.main' : 'text.primary'
                              }}
                            >
                              {change.previous === null ? '—' : 
                               typeof change.previous === 'object' ? 
                               JSON.stringify(change.previous) : 
                               String(change.previous)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontFamily: 'monospace',
                                color: change.type === 'added' ? 'success.main' : 'text.primary'
                              }}
                            >
                              {change.new === null ? '—' : 
                               typeof change.new === 'object' ? 
                               JSON.stringify(change.new) : 
                               String(change.new)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={change.type}
                              size="small"
                              color={
                                change.type === 'added' ? 'success' :
                                change.type === 'removed' ? 'error' :
                                'warning'
                              }
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}

          {/* Show raw data only if there are no field changes or if it's a create action */}
          {(fieldChanges.length === 0 || auditTrail.action === 'create') && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Raw Data
              </Typography>
              {auditTrail.action === 'create' ? (
                renderJsonData(auditTrail.new_data, 'Created Data')
              ) : (
                <>
                  {renderJsonData(auditTrail.old_data, 'Previous Data')}
                  {renderJsonData(auditTrail.new_data, 'New Data')}
                </>
              )}
            </Grid>
          )}

          {/* Metadata */}
          {auditTrail.audit_metadata && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Additional Metadata
              </Typography>
              {renderJsonData(auditTrail.audit_metadata, 'Metadata')}
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuditTrailDetailModal;